import { ApiResponse } from "../models/api-response";
import { CloudServices } from "../models/cloud-service";
import { RequestOptions } from "../models/request-options";
import { getNoodlContext } from "./noodlContext";

/**
 * Client class to handle API requests
 */
export class Client {
  getCloudServices(): CloudServices | null {
    const context = getNoodlContext();
    if (!context?.graphModel) {
      return null;
    }
    return context.graphModel.getMetaData('cloudservices') || null;
  }

  getAppId(cloudServices: CloudServices): string {
    return cloudServices.appId ||
      `${cloudServices.workspaceId}-${cloudServices.instanceId}`;
  }

  getSessionToken(appId: string): string | null {
    try {
      const key = `Parse/${appId}/currentUser`;
      const currentUserData = localStorage.getItem(key);
      if (currentUserData) {
        const currentUser = JSON.parse(currentUserData);
        return currentUser.sessionToken || null;
      }
    } catch {
      // Failed to extract session token
    }
    return null;
  }

  makeRequest<T>(path: string, options: RequestOptions): Promise<T> {
    return new Promise((resolve, reject) => {
      const cloudServices = this.getCloudServices();

      if (!cloudServices) {
        reject(new Error('No active cloud services in this project'));
        return;
      }

      const appId = this.getAppId(cloudServices);
      const endpoint = cloudServices.endpoint;
      
      if (!endpoint) {
        reject(new Error('No endpoint configured in cloud services'));
        return;
      }

      const xhr = new XMLHttpRequest();

      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          let result: ApiResponse<T> | string;
          try {
            result = JSON.parse(xhr.response);
          } catch {
            result = xhr.response;
          }
          if (xhr.status === 200 || xhr.status === 201) {
            if (typeof result === 'object' && result !== null && 'result' in result) {
              resolve(result.result);
            } else {
              resolve(result as T);
            }
          } else {
            const errorMessage = typeof result === 'object' && result !== null && 'error' in result
              ? (result as { error: string }).error
              : `Request failed: ${xhr.status}`;
            reject(new Error(errorMessage));
          }
        }
      };

      xhr.open(options.method || 'GET', endpoint + path, true);
      xhr.setRequestHeader('X-Parse-Application-Id', appId);
      xhr.setRequestHeader('Content-Type', 'application/json');

      const sessionToken = this.getSessionToken(appId);
      if (sessionToken) {
        xhr.setRequestHeader('X-Parse-Session-Token', sessionToken);
      }

      if (options.content) {
        xhr.send(JSON.stringify(options.content));
      } else {
        xhr.send();
      }
    });
  }
}
